import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Layout, Typography, Spin, Button, Modal, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import api from '~/lib/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useUIStore } from '@stores/ui';
import SparkyPlayer from '@devunlimit/compose-frontend-player';
import useVideoContext from '@lib/hooks/useVideoContext';
import { scMixinAbsoluteFull } from '@utils/emotion/mixins';
import { scFunctionZIndex } from '@utils/emotion/functions';
import { getHighestQualityVideoItem } from '~/utils';

import { ReactComponent as IconPublishedPublic } from '@svg/icon-published-public.svg';
import { ReactComponent as IconPublishedPrivate } from '@svg/icon-published-private.svg';

const { Content: AntdContent } = Layout;
const { Title, Text } = Typography;

type MatchParams = {
  videoId: string;
};

const Visibility: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { videoId } = useParams<keyof MatchParams>();
  const { setOverrideTheme } = useUIStore();
  const [{ data, loading, error }, fetch] = useVideoContext(videoId);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isPublic, setPublic] = useState(false);
  const [isUnauthorizedAccess, setUnauthorizedAccess] = useState(false);

  useEffect(() => {
    if (data && !loading && !error) {
      if (data.jobs.transcoding === 'processing') {
        Modal.warn({
          title: t('error.api.common.retrieve.server.title'),
          content: t('error.api.edit.ontranscoding'),
          okText: t('error.back'),
          onOk: () => {
            navigate(-1);
          },
        });

        return;
      }

      if (data.jobs.transcoding === 'pending' || !data.categories.length) {
        setUnauthorizedAccess(true);
        Modal.error({
          title: t('error.api.common.retrieve.server.title'),
          content: t('error.api.common.retrieve.server.content'),
          okText: t('a11y.ok'),
          onOk: () => {
            navigate(-1);
          },
        });

        return;
      }

      setPublic(data.published);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error]);

  useEffect(() => {
    setOverrideTheme('dark');

    return () => {
      setOverrideTheme(null);
    };
  }, [setOverrideTheme]);

  const submit = () => {
    if (!data || isUnauthorizedAccess) return;

    setSubmitting(true);
    api
      .patch(`/video/${data.videoId}`, {
        published: isPublic,
      })
      .then(() => {
        setSubmitting(false);
        fetch({ silent: true });
        message.success(t('video.edit.publishing.meesage.success'), 4);
      })
      .catch((e) => {
        Modal.error({
          title: t('error.api.common.submit.server.title'),
          content: t('error.api.common.submit.server.content'),
          okText: t('a11y.ok'),
        });
        setSubmitting(false);
        console.error(e);
      });
  };

  if (loading || error || !data) {
    return (
      <Content>
        <div className="loading-screen">
          <div className="dimmer" />
          <div className="inner">
            <Spin
              indicator={
                <LoadingOutlined
                  style={{
                    fontSize: 48,
                    color: 'rgb(255, 255, 255, 0.75)',
                  }}
                />
              }
            />
          </div>
        </div>
      </Content>
    );
  }

  return (
    <Content>
      <HeaderBlock>
        <Typography>
          <Title>{t('video.edit.publishing.title')}</Title>
          <Text>{t('video.edit.publishing.desc')}</Text>
        </Typography>
      </HeaderBlock>
      <PreviewBlock>
        <SparkyPlayer
          video={{ src: getHighestQualityVideoItem(data.transcodings)! }}
          options={{ autoplay: false }}
        />
      </PreviewBlock>
      <ActionBlock>
        <div className="btn-area">
          <button
            type="button"
            className="btn"
            disabled={
              data.jobs.poseEstimation === 'processing' ||
              (data.type === 'pose' && !data.pose?.chapters) ||
              isSubmitting
            }
            aria-pressed={isPublic ? 'false' : 'true'}
            onClick={() => {
              setPublic(false);
            }}
          >
            <span className="icon">
              <IconPublishedPrivate />
            </span>
            {t('video.edit.form.published.radio.no')}
          </button>
          <button
            type="button"
            className="btn"
            disabled={
              data.jobs.poseEstimation === 'processing' ||
              (data.type === 'pose' && !data.pose?.chapters) ||
              isSubmitting
            }
            aria-pressed={isPublic ? 'true' : 'false'}
            onClick={() => {
              setPublic(true);
            }}
          >
            <span className="icon">
              <IconPublishedPublic />
            </span>
            {t('video.edit.form.published.radio.yes')}
          </button>
        </div>
        <div className="submit-area">
          {data.jobs.poseEstimation === 'processing' && (
            <p className="msg-disabled">{t('video.edit.publishing.warn.processing')}</p>
          )}
          {data.type === 'pose' && !data.pose?.chapters && (
            <p className="msg-disabled">{t('video.edit.publishing.warn.nochapter')}</p>
          )}
          {data.type === 'pose' && !data.pose?.chapters ? (
            <Button
              htmlType="button"
              type="primary"
              size="large"
              style={{
                minWidth: 160,
                backgroundColor: '#52C41A',
                borderColor: '#52C41A',
                color: '#090909',
              }}
              onClick={() => {
                navigate(`/video/${videoId}/editor/chapter`);
              }}
            >
              {t('video.edit.publishing.gochapteredit')} &gt;
            </Button>
          ) : (
            <Button
              htmlType="button"
              className="custom-disabled-style"
              type="primary"
              size="large"
              style={{
                minWidth: 160,
                backgroundColor: '#52C41A',
                borderColor: '#52C41A',
                color: '#090909',
              }}
              loading={isSubmitting}
              disabled={data.jobs.poseEstimation === 'processing'}
              onClick={() => submit()}
            >
              {t('a11y.submit')}
            </Button>
          )}
        </div>
      </ActionBlock>
    </Content>
  );
};

const Content = styled(AntdContent)`
  display: flex;
  flex-direction: column;

  .loading-screen {
    display: flex;
    justify-content: center;
    align-items: center;
    ${scMixinAbsoluteFull};
    ${scFunctionZIndex('loading')};

    .dimmer {
      ${scMixinAbsoluteFull};
      z-index: 0;
      background-color: rgba(0, 0, 0, 0.6);
    }

    .inner {
      position: relative;
      z-index: 1;
    }
  }
`;

const HeaderBlock = styled.div`
  flex: 0 1 auto;
  display: block;
  position: relative;
  padding: 18px 36px 30px;

  h1.ant-typography {
    font-weight: bold;
    font-size: 23px;
    line-height: 1.5;
    letter-spacing: 0.01em;
  }

  span.ant-typography {
    font-size: 14px;
    line-height: 1.5;
    letter-spacing: 0.01em;
    white-space: pre;
    color: #b6b7b8;
  }
`;

const PreviewBlock = styled.div`
  overflow: hidden;
  flex: 1 1 auto;
`;

const ActionBlock = styled.div`
  padding: 51px 38px 69px;

  .btn-area {
    padding: 17px 0;
    border: 1px solid #424242;
    text-align: center;
    color: #fff;
  }

  .btn {
    min-width: 251px;
    padding: 20px 50px;
    border-radius: 3px;
    background-color: #090909;
    font-weight: bold;
    font-size: 16px;
    color: #424242;
    cursor: pointer;
    outline: none;

    & + .btn {
      margin-left: 24px;
    }

    &:disabled {
      cursor: default;
    }

    &:disabled:hover {
      background-color: #090909;
      color: #424242;
    }

    &[aria-pressed='true'] {
      background-color: #8100ff;
      color: #f5f6f8;

      &:disabled:hover {
        background-color: #8100ff;
        color: #f5f6f8;
      }
    }

    &[aria-pressed='false'] {
      .icon svg path {
        fill: #424242;
      }

      &:not(:disabled):hover {
        background-color: #424242;
        color: #f5f6f8;

        .icon svg path {
          fill: #f5f6f8;
        }
      }
    }

    .icon {
      display: inline-block;
      position: relative;
      top: 3px;
      margin-right: 6px;
      vertical-align: top;

      svg {
        vertical-align: top;

        path {
          fill: #f5f6f8;
        }
      }
    }
  }

  .submit-area {
    text-align: center;

    .ant-btn {
      margin-top: 52px;
    }
  }

  .msg-disabled {
    margin: 14px 0 0;
    font-size: 15px;
    line-height: 1.5;
    color: #cc1937;

    & + .ant-btn {
      margin-top: 16px;
    }
  }
`;

export default Visibility;
